import React, { useState } from "react";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MoreVertIcon from "../../assets/icons/MoreVertIcon.svg";
import EditIcon from "../../assets/icons/Icon-edit.svg";
import DeleteIcon from "../../assets/icons/Icon-delete.svg";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useFormContext } from 'react-hook-form';
import { OutletContext } from "../../App";
import DialogEditComponent from "../segmentSummary/DialogEditSegment";
import DialogDeleteComponent from "../segmentSummary/DialogDeleteComponent";
import { useAppMe } from "../../services/okta";
import { useUpdateSegmentEndDate } from "../../services/segment/index";
import { FormDataFilterSegment } from "./segment-interface";
import { useSimulationParams } from "../../shared/hooks/useSimulationParams";

interface OptionsMenuProps {
  id: any;
  pathname?: any;
  usedInCampaign?: string;
  name: string;
}

const OptionsMenu = ({ id, pathname, usedInCampaign, name }: OptionsMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { setValue, getValues } = useFormContext<FormDataFilterSegment>();
  const { setAlert } = useOutletContext<OutletContext>();
  const { data: dataAppMe } = useAppMe({});
  const user = dataAppMe?.data?.user_id;
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const { createLink } = useSimulationParams();

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    setShowEditSegmentDialog(true);
    handleClose();
  };

  const { mutate: deleteSegment, isPending: isPendingDeleteSegment } = useUpdateSegmentEndDate({
    user: user,
    segmentName: name,
    segmentId: id,
    options: {
      onSuccess: (res: any) => {
        const status = res?.data?.status;
        const message = res?.data?.message;
        const currentRefresh = getValues('refreshKey') || 0;
        if (status === "Error") {
          setAlert({
            alertType: "warning",
            alertMsg: message || "Segment not deleted successfully",
          });
        } else {
          setAlert({
            alertType: "success",
            alertMsg: message || "Segment deleted successfully",
          });
        }
        setValue('refreshKey', currentRefresh + 1);
        setValue('deletePending', false);
      },
      onError: (err: any) => {
        console.error("Delete error details:", err);
        setAlert({
          alertType: "error",
          alertMsg: `Error deleting Segment: ${err?.message || err}`,
        });
        setValue('deletePending', false);
      },
    },
  });

  const handleDeleteClick = () => {
    setShowDeleteSegmentDialog(true);
    handleClose();
  };

  const handleDelete = () => {
    setShowDeleteSegmentDialog(false);
    if (id && user && name) {
      setValue('deletePending', true);
      deleteSegment({
        segmentId: id,
        user: user,
        segmentName: name,
      });
    }
    handleClose();
  };

  const [showEditSegmentDialog, setShowEditSegmentDialog] = useState(false);
  const [showDeleteSegmentDialog, setShowDeleteSegmentDialog] = useState(false);

  const closeEditSegmentDialog = () => {
    setShowEditSegmentDialog(false);
  };

  const closeDeleteSegmentDialog = () => {
    setShowDeleteSegmentDialog(false);
  };

  const navigateOnSegment = () => {
    navigate(createLink(`/create/segment?id=${id}`));
  };

  return (
    <>
      <IconButton
        aria-label="more"
        aria-controls={open ? "options-menu" : undefined}
        aria-haspopup="true"
        onClick={handleOpen}
        size="small"
      >
        <img className="no-border__icon" src={MoreVertIcon} alt="Folder Icon" />
      </IconButton>
      <Menu
        id="options-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{ borderRadius: 12 }}
      >
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <img className="no-border__icon" src={EditIcon} alt="Folder Icon" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>
          <ListItemIcon>
            <img className="no-border__icon" src={DeleteIcon} alt="Folder Icon" />
          </ListItemIcon>
          <ListItemText sx={{ color: "#F1181B" }}>Delete</ListItemText>
        </MenuItem>
      </Menu>

      <DialogEditComponent
        open={showEditSegmentDialog}
        onClose={closeEditSegmentDialog}
        onEdit={navigateOnSegment}
        type="segment"
        campaignNames={
          usedInCampaign
            ? usedInCampaign
              .split(",")
              .map(x => x.trim())
              .filter(x => x !== "" && x !== "-")
            : []
        }
      />
      <DialogDeleteComponent
        open={showDeleteSegmentDialog}
        onClose={closeDeleteSegmentDialog}
        onDelete={handleDelete}
        type="segment"
        campaignNames={usedInCampaign?.split(", ")}
        name={name}
      />
    </>
  );
};

export default OptionsMenu;
